import { useReducer } from 'react';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  useToast,
} from '@chakra-ui/react';
import { submitRfiDetails } from 'api/kyc';
import { showError, showToast } from 'utils/notifications';
import KycRfiResponse from 'components/KycRfi/KycRfiResponse';
import StepNavigator from 'components/KycRfi/StepNavigator';

const initialState = {
  currentStep: 0,
  responses: {},
  loading: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'NEXT_STEP':
      return { ...state, currentStep: state.currentStep + 1 };
    case 'PREV_STEP':
      return { ...state, currentStep: state.currentStep - 1 };
    case 'UPDATE_RESPONSE':
      return {
        ...state,
        responses: { ...state.responses, [action.payload.id]: action.payload.value },
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
}

const KycRfiSubmissionModal = ({ isOpen, onClose, rfilist, documentInfo, entityId, parentId, fetchKycDetails }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const toast = useToast();

  const handleAnswerChange = (id, value, document) => {
    dispatch({
      type: 'UPDATE_RESPONSE',
      payload: {
        id,
        value: {
          id: id,
          rfi_details: {
            notes: value,
            document: document,
          },
        },
      },
    });
  };

  const isCurrentQuestionAnswered = () => {
    const question = rfilist[state.currentStep];
    const responseForQuestion = state.responses[question.id];

    if (!responseForQuestion) return false;
    if (!question?.prompt_format.is_document_required) return !!responseForQuestion?.rfi_details?.notes?.trim();
    if (question?.prompt_format.is_front_back_required) return responseForQuestion?.rfi_details?.document?.length === 2;

    return responseForQuestion?.rfi_details?.document?.length > 0;
  };

  const handleSubmit = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const payload = { parent_id: parentId, rfi_submissions: Object.values(state.responses) };

    try {
      await submitRfiDetails(entityId, payload);
      showToast(toast, 'RFI submitted successfully', 'success');
      fetchKycDetails();
      onClose();
    } catch (error) {
      showError(toast, 'Error submitting RFI', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Submit RFIs</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Progress value={(state.currentStep / rfilist.length) * 100} size='sm' colorScheme='blue' mb={4} />
          {rfilist?.length > 0 && (
            <KycRfiResponse
              key={state.currentStep}
              rfi={rfilist[state.currentStep]}
              documentInfo={documentInfo}
              questionNo={state.currentStep + 1}
              onAnswerChange={handleAnswerChange}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <StepNavigator
            onNext={() => dispatch({ type: 'NEXT_STEP' })}
            onPrev={() => dispatch({ type: 'PREV_STEP' })}
            isNextDisabled={!isCurrentQuestionAnswered() || state.currentStep === rfilist.length - 1}
            isLast={state.currentStep === rfilist.length - 1}
            isFirst={state.currentStep === 0}
            handleSubmit={handleSubmit}
            loading={state.loading}
            isCurrentQuestionAnswered={isCurrentQuestionAnswered()}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default KycRfiSubmissionModal;
